/**
 * Team member rate overview table column definitions and components.
 *
 * This module creates dynamic table columns for displaying project team members
 * with their rate configurations in the billing tab. Supports multiple billing
 * models and rate sources with interactive editing capabilities.
 *
 * Key Features:
 * - Dynamic rate source selection per team member when using ProjectRateCard
 * - Inline rate editing for custom project rates
 * - Role-based access control (read/edit modes)
 * - Team member management (add/remove manually added members)
 * - Multi-currency support with real-time currency display
 * - Integration with project billing business logic
 */
import { useEffect, useRef } from 'react';

import { ColumnDef } from '@tanstack/react-table';
import { AlertCircle, Trash2 } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import i18n from '@/core/i18n';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { ProjectBillingFormData } from '@/modules/projects/types';
import {
  ProjectTeamMember,
  ProjectTeamMemberType,
} from '@/modules/projects/types/project-team-member';

/**
 * Context interface providing all necessary data and handlers for table column rendering.
 * Passed from parent billing component to enable dynamic column behavior based on
 * current billing configuration and user permissions.
 */
interface TeamMemberColumnContext {
  /** Translation function for internationalized text */
  t: (key: string) => string;
  /** Current project-level rate source setting */
  currentRateSource: RateSource;
  /** Current form values including currency and rates */
  currentValues: ProjectBillingFormData;
  /** Whether the form is in read-only mode */
  isReadMode: boolean;
  /** Available rate source options for project-level selection */
  rateSourceOptions: Array<{ value: RateSource; label: string }>;
  /** Available rate source options for individual team members */
  teamMemberRateSourceOptions: Array<{ value: RateSource; label: string }>;
  /** Function to determine effective rate source for a team member */
  getEffectiveRateSource: (member: ProjectTeamMember) => RateSource;
  /** Function to calculate current rate value for a team member */
  calculateMemberRate: (member: ProjectTeamMember) => number;
  /** Handler for changing team member's rate source */
  handleRateSourceChange: (memberId: string, newRateSource: RateSource) => void;
  /** Handler for changing team member's custom rate value */
  handleCustomRateChange: (memberId: string, newRate: number) => void;
  /** Handler for removing manually added team members */
  handleRemoveMember: (memberId: string) => void;
  /** Whether rates are being refetched due to currency change */
  isRefetchingRates: boolean;
  /** Error that occurred during rate refetch */
  rateRefetchError: Error | null;
}

/**
 * Props for the inline rate source selector component used in table cells.
 * Enables team members to have individual rate sources when project uses ProjectRateCard.
 */
interface RateSourceSelectorProps {
  /** Current selected rate source value */
  value: RateSource;
  /** Available rate source options for selection */
  options: Array<{ value: RateSource; label: string }>;
  /** Callback when rate source selection changes */
  onChange: (value: RateSource) => void;
  /** Whether the selector should be disabled */
  disabled?: boolean;
}

interface CustomRateInputProps {
  value: number;
  currency?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}

/**
 * Inline rate source selector component for individual team member rows.
 *
 * Used when project rate source is set to ProjectRateCard, allowing each team member
 * to have their own rate source selection. Uses its own form state to manage the
 * selection and syncs with parent component through onChange callback.
 *
 * Implementation uses React Hook Form with useWatch for real-time updates and
 * bi-directional synchronization between internal form state and external props.
 */
function RateSourceSelector({ value, options, onChange, disabled }: RateSourceSelectorProps) {
  // Local form state for this rate source selector
  const rowForm = useForm({
    defaultValues: { rateSource: value },
  });
  const isSyncingFromParentRef = useRef(false);

  // Sync internal form state when external value changes
  useEffect(() => {
    if (rowForm.getValues('rateSource') === value) return;

    isSyncingFromParentRef.current = true;
    rowForm.setValue('rateSource', value);
  }, [value, rowForm]);

  // Watch for internal changes and propagate to parent
  const watchedValue = useWatch({ control: rowForm.control, name: 'rateSource' });
  useEffect(() => {
    if (isSyncingFromParentRef.current) {
      if (watchedValue === value) {
        isSyncingFromParentRef.current = false;
      }
      return;
    }

    if (watchedValue && watchedValue !== value) {
      onChange(watchedValue as RateSource);
    }
  }, [watchedValue, value, onChange]);

  return (
    <div className="w-48">
      <SingleSelect
        name="rateSource"
        control={rowForm.control}
        options={options}
        searchable={false}
        showAvatars={false}
        showDescriptionInOptions={false}
        showDescriptionInSelector={false}
        disabled={disabled}
        className="h-8"
        isClearSelectionEnabled={false}
      />
    </div>
  );
}

function CustomRateInput({ value, currency, onChange, disabled }: CustomRateInputProps) {
  const rowForm = useForm({
    defaultValues: { customRate: value },
  });
  const isSyncingFromParentRef = useRef(false);

  useEffect(() => {
    if (rowForm.getValues('customRate') === value) return;

    isSyncingFromParentRef.current = true;
    rowForm.setValue('customRate', value);
  }, [value, rowForm]);

  const watchedValue = useWatch({ control: rowForm.control, name: 'customRate' });

  useEffect(() => {
    if (isSyncingFromParentRef.current) {
      if (watchedValue === value) {
        isSyncingFromParentRef.current = false;
      }
      return;
    }

    if (typeof watchedValue === 'number' && watchedValue !== value) {
      onChange(watchedValue);
    }
  }, [watchedValue, value, onChange]);

  return (
    <div className="w-140">
      <InputFieldWithCurrency
        name="customRate"
        control={rowForm.control}
        currency={currency}
        isReadMode={Boolean(disabled)}
        maxDecimalPlaces={2}
      />
    </div>
  );
}

/**
 * Factory function that creates table column definitions for team member rate overview.
 *
 * Generates dynamic columns based on current billing configuration and user permissions.
 * Each column adapts its behavior based on the provided context, supporting different
 * billing models, rate sources, and access levels.
 *
 * Column Structure:
 * 1. **Team Member**: Avatar + name display
 * 2. **Rate Source**: Shows current rate source or provides selection dropdown
 * 3. **Rate**: Displays calculated rate or provides inline editing for custom rates
 * 4. **Actions**: Shows member status or removal option for manually added members
 *
 * @param context - Complete context object with data, handlers, and configuration
 * @returns Array of column definitions for React Table
 */
export const createTeamMemberColumns = (
  context: TeamMemberColumnContext
): ColumnDef<ProjectTeamMember>[] => [
  {
    accessorKey: 'name',
    header: context.t('details.tabs.billing.teamMember'),
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <EntityAvatar
          image={row.original.avatarLink}
          name={row.original.name}
          size="sm"
          className="flex-shrink-0"
        />
        <span className="font-medium">{row.original.name}</span>
      </div>
    ),
  },
  {
    accessorKey: 'rateSource',
    header: context.t('details.tabs.billing.rateSource.label'),
    cell: ({ row }) => {
      const effectiveRateSource = context.getEffectiveRateSource(row.original);

      // Show read-only rate source label when in read mode or not using ProjectRateCard
      if (context.isReadMode || context.currentRateSource !== RateSource.ProjectRateCard) {
        return (
          <span className="whitespace-nowrap text-sm text-foreground">
            {context.rateSourceOptions.find(option => option.value === effectiveRateSource)?.label}
          </span>
        );
      }

      // Show interactive rate source selector for ProjectRateCard in edit mode
      return (
        <RateSourceSelector
          value={effectiveRateSource}
          options={context.teamMemberRateSourceOptions}
          onChange={value => context.handleRateSourceChange(row.original.id, value)}
          disabled={context.isReadMode}
        />
      );
    },
  },
  {
    accessorKey: 'rate',
    header: context.t('details.tabs.billing.rate'),
    cell: ({ row }) => {
      const effectiveRateSource = context.getEffectiveRateSource(row.original);
      const currentRate = context.calculateMemberRate(row.original);

      // Show loading skeleton during currency refetch
      if (context.isRefetchingRates) {
        return <Skeleton className="h-5 w-24" />;
      }

      // Show error state if refetch failed
      if (context.rateRefetchError) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-3 w-3 text-destructive" />
                <span className="text-xs text-destructive">
                  {context.t('details.tabs.billing.rateLoadFailed')}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>
                <p>{context.t('details.tabs.billing.tryAgainLater')}</p>
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        );
      }

      // Show editable rate input for custom rate in edit mode
      if (
        !context.isReadMode &&
        context.currentRateSource === RateSource.ProjectRateCard &&
        effectiveRateSource === RateSource.Custom
      ) {
        const isMasked = RateMask.isMasked(row.original.customRateValue);
        return isMasked ? (
          <span className="whitespace-nowrap">
            {i18n.t('common:currencyRate', {
              rate: RateMask.apply(row.original.customRateValue),
              currency: context.currentValues.currency,
            })}
          </span>
        ) : (
          <CustomRateInput
            value={row.original.customRateValue ?? 0}
            currency={context.currentValues.currency}
            onChange={value => context.handleCustomRateChange(row.original.id, value)}
            disabled={context.isReadMode}
          />
        );
      }

      // Show calculated rate with currency for all other cases
      return (
        <span className="whitespace-nowrap">
          {i18n.t('common:currencyRate', {
            rate: RateMask.apply(currentRate),
            currency: context.currentValues.currency,
          })}
        </span>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: ({ column }) => (
      <div className="w-full text-right">{context.t('details.tabs.billing.infoAction')}</div>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-end">
        {/* Show badge for members with existing assignments (cannot be removed) */}
        {row.original.type === ProjectTeamMemberType.HasAssignments ? (
          <Badge variant="secondary" className="whitespace-nowrap text-xs font-normal">
            {context.t('details.tabs.billing.hasAssignments')}
          </Badge>
        ) : row.original.type === ProjectTeamMemberType.ManuallyAdded && !context.isReadMode ? (
          /* Show remove button for manually added members in edit mode */
          <Button
            variant="ghost"
            size="sm"
            onClick={() => context.handleRemoveMember(row.original.id)}
            className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    ),
    size: 140,
  },
];

// Export the context interface for use in parent components
export type { TeamMemberColumnContext };
